/* eslint-disable react-hooks/exhaustive-deps */
import TimeCell from "../../components/TimeCell";
import { useState, useEffect, useMemo } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DataGridButton from "../../components/DataGridButton";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Sync } from "@mui/icons-material";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import PrFilterBar from "./PrFilterBar";
import { useGetPrBuilds } from "../../api/pullRequests";
import RedirectLink from "../../components/RedirectLink";
import { githubUrl } from "../../utils/urls";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import { dataGridButtonStyles } from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import { badges } from "../../styles/badgeStyles";
import utilColors from "../../styles/utilColors";
import styles from "./styles";

const MergedPullRequestBuilds = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [prs, setPrs] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});

  const {
    postData: getPrs,
    response: getPrsResp,
    isLoading: isLoadingPrs,
  } = useGetPrBuilds();

  useEffect(() => {
    getPrs();
  }, []);

  useEffect(() => {
    if (Array.isArray(getPrsResp?.response)) {
      setPrs(getPrsResp.response);
    }
  }, [getPrsResp]);

  // Filter options derived from the loaded rows.
  const filters = useMemo(() => {
    const distinct = (field) =>
      [...new Set(prs.map((pr) => pr?.[field]).filter(Boolean))].sort();
    return [
      { name: "repoName", label: "Repository", options: distinct("repoName") },
      { name: "baseBranch", label: "Base", options: distinct("baseBranch") },
      {
        name: "buildStatus",
        label: "Status",
        options: distinct("buildStatus"),
      },
    ];
  }, [prs]);

  const filteredPrs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return prs.filter(
      (pr) =>
        (!query || pr?.commitMessage?.toLowerCase().includes(query)) &&
        Object.entries(selectedFilters).every(
          ([field, values]) => !values?.length || values.includes(pr?.[field])
        )
    );
  }, [prs, search, selectedFilters]);

  const columns = [
    {
      field: "repoName",
      renderHeader: () => "Repository Name",
      flex: 2.5,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { repoName } }) => repoName || "",
    },
    {
      field: "commitMessage",
      flex: 4,
      renderHeader: () => "PR Title",
      renderCell: ({ row: { commitMessage } }) => commitMessage ?? "",
    },
    {
      field: "tag",
      flex: 1,
      renderHeader: () => "Tag",
      renderCell: ({ row: { commitHash, repoName, tag } }) => (
        <RedirectLink
          href={`${githubUrl}/${repoName}/commit/${commitHash}`}
          color={utilColors(colors).skyBlue}
        >
          <Box component="span" sx={badges(colors).neutral}>
            {tag ?? ""}
          </Box>
        </RedirectLink>
      ),
    },
    {
      field: "baseBranch",
      flex: 1,
      renderHeader: () => "Base",
      renderCell: ({ row: { baseBranch } }) => baseBranch ?? "",
    },
    {
      field: "buildStatus",
      flex: 1.5,
      renderHeader: () => "Status",
      renderCell: ({ row: { buildStatus } }) => (
        <StatusChip value={buildStatus} />
      ),
    },
    {
      field: "createdAt",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Created At",
      renderCell: ({ row: { createdAt } }) => <TimeCell value={createdAt} />,
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title="Merged Pull Request Builds"
            subtitle="See the list of non-successful builds of merged pull requests"
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => getPrs()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>
      <PrFilterBar
        filters={filters}
        selected={selectedFilters}
        onChange={(name, values) =>
          setSelectedFilters((prev) => ({ ...prev, [name]: values }))
        }
        onClearAll={() => setSelectedFilters({})}
        search={search}
        onSearchChange={setSearch}
        filteredCount={filteredPrs.length}
        totalCount={prs.length}
        itemsNoun="builds"
      />
      <DataTable
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => `${row?.repoName}/${row.commitHash}`}
        loading={isLoadingPrs}
        rows={filteredPrs}
        columns={columns}
      />
    </Box>
  );
};

export default MergedPullRequestBuilds;
